import { Outlet, Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Sparkles, Heart, User, LogOut, Menu, X, CalendarDays, Shield } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import CreditsBalance from '@/components/CreditsBalance';
import BrandLogo from '@/components/BrandLogo';
import PromoBanner from '@/components/PromoBanner';

const NAV_ITEMS = [
  { to: '/', label: 'Home', end: true },
  { to: '/diy', label: 'DIY Lab' },
  { to: '/products', label: 'Curator' },
  { to: '/regimen', label: 'Regimen' },
  { to: '/ask', label: 'Ask AI' },
  { to: '/styling', label: 'Styling' },
  { to: '/pricing', label: 'Pricing' },
];

const NavItem = ({ to, label, end, onClick }) => (
  <NavLink
    to={to}
    end={end}
    onClick={onClick}
    data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}-link`}
    className={({ isActive }) =>
      `text-sm transition-colors px-3 py-2 rounded-md ${
        isActive
          ? 'text-foreground bg-secondary'
          : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60'
      }`
    }
  >
    {label}
  </NavLink>
);

const Brand = () => (
  <BrandLogo size={36} variant="lockup" showTagline className="" />
);

export default function AppShell() {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const isAuthPage = location.pathname === '/login' || location.pathname === '/signup';

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <PromoBanner />
      <header
        data-testid="top-nav"
        className="sticky top-0 z-40 bg-background/90 backdrop-blur supports-[backdrop-filter]:bg-background/75 border-b border-border/70"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Brand />
          <nav className="hidden md:flex items-center gap-1">
            {NAV_ITEMS.map((it) => (
              <NavItem key={it.to} {...it} />
            ))}
          </nav>
          <div className="hidden md:flex items-center gap-2">
            {user ? (
              <>
                <CreditsBalance />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-2 rounded-full pl-1 pr-3"
                      data-testid="user-menu-button"
                    >
                      <Avatar className="h-7 w-7">
                        <AvatarFallback className="bg-rose-tint text-ink text-xs font-semibold">
                          {(user.name || user.email)?.[0]?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm">{user.name?.split(' ')[0] || 'You'}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel className="font-normal">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">{user.name || 'Beauty insider'}</span>
                        <span className="text-xs text-muted-foreground">{user.email}</span>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/credits')} data-testid="menu-credits">
                      <Sparkles className="h-4 w-4 mr-2" /> Buy credits
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/regimen')} data-testid="menu-regimen">
                      <CalendarDays className="h-4 w-4 mr-2" /> My Regimen
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="menu-profile">
                      <User className="h-4 w-4 mr-2" /> Beauty Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/next-steps')} data-testid="menu-next-steps">
                      <Sparkles className="h-4 w-4 mr-2" /> What to try next
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/favorites')} data-testid="menu-favorites">
                      <Heart className="h-4 w-4 mr-2" /> My Favorites
                    </DropdownMenuItem>
                    {user.is_admin && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                          <Shield className="h-4 w-4 mr-2" /> Admin dashboard
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                      <LogOut className="h-4 w-4 mr-2" /> Sign out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  className="rounded-xl"
                  onClick={() => navigate('/login')}
                  data-testid="nav-login-button"
                >
                  Sign in
                </Button>
                <Button
                  size="sm"
                  className="rounded-xl bg-primary text-primary-foreground hover:bg-primary/90"
                  onClick={() => navigate('/signup')}
                  data-testid="nav-signup-button"
                >
                  Join Radiance
                </Button>
              </>
            )}
          </div>
          {/* Mobile */}
          <div className="md:hidden flex items-center gap-2">
            {user && <CreditsBalance />}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-full"
                  data-testid="mobile-nav-open-button"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-background w-72">
                <SheetHeader>
                  <SheetTitle className="font-display text-2xl text-left">Radiance Hub</SheetTitle>
                </SheetHeader>
                <nav className="mt-6 flex flex-col gap-1">
                  {NAV_ITEMS.map((it) => (
                    <NavItem key={it.to} {...it} onClick={() => setMobileOpen(false)} />
                  ))}
                  <div className="my-3 border-t border-border" />
                  {user ? (
                    <>
                      <button
                        className="text-left px-3 py-2 text-sm hover:bg-secondary rounded-md flex items-center gap-2"
                        onClick={() => {
                          setMobileOpen(false);
                          navigate('/credits');
                        }}
                        data-testid="mobile-menu-credits"
                      >
                        <Sparkles className="h-4 w-4 text-rose-gold" /> Buy credits
                      </button>
                      <button
                        className="text-left px-3 py-2 text-sm hover:bg-secondary rounded-md"
                        onClick={() => {
                          setMobileOpen(false);
                          navigate('/profile');
                        }}
                      >
                        Beauty Profile
                      </button>
                      <button
                        className="text-left px-3 py-2 text-sm hover:bg-secondary rounded-md"
                        onClick={() => {
                          setMobileOpen(false);
                          handleLogout();
                        }}
                      >
                        Sign out
                      </button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="outline"
                        className="rounded-xl"
                        onClick={() => {
                          setMobileOpen(false);
                          navigate('/login');
                        }}
                      >
                        Sign in
                      </Button>
                      <Button
                        className="rounded-xl bg-primary text-primary-foreground hover:bg-primary/90"
                        onClick={() => {
                          setMobileOpen(false);
                          navigate('/signup');
                        }}
                      >
                        Join Radiance
                      </Button>
                    </>
                  )}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      {!isAuthPage && (
        <footer className="border-t border-border/70 bg-cream-paper mt-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <Brand />
              <p className="mt-3 text-sm text-muted-foreground max-w-sm">
                A quiet-luxury beauty journal for DIY rituals and curated finds. Made for women who care
                about their skin and their time.
              </p>
              <p className="mt-3 text-xs text-muted-foreground">
                Radiance Hub · Mumbai, Maharashtra, India
              </p>
              <a
                href="mailto:Webuser@radiancehub.org"
                className="mt-1 block text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground"
                data-testid="footer-support-email"
              >
                Webuser@radiancehub.org
              </a>
            </div>
            <div>
              <h4 className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">Discover</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/diy" className="hover:text-primary">DIY Beauty Lab</Link></li>
                <li><Link to="/products" className="hover:text-primary">Smart Product Curator</Link></li>
                <li><Link to="/community" className="hover:text-primary">Community reviews</Link></li>
                {user && <li><Link to="/favorites" className="hover:text-primary">My Favorites</Link></li>}
              </ul>
            </div>
            <div>
              <h4 className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="hover:text-primary" data-testid="footer-about-link">About</Link></li>
                <li><Link to="/pricing" className="hover:text-primary" data-testid="footer-pricing-link">Pricing</Link></li>
                <li><Link to="/contact" className="hover:text-primary" data-testid="footer-contact-link">Contact</Link></li>
                {!user && <li><Link to="/signup" className="hover:text-primary">Create account</Link></li>}
              </ul>
            </div>
            <div>
              <h4 className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/terms" className="hover:text-primary" data-testid="footer-terms-link">Terms &amp; Conditions</Link></li>
                <li><Link to="/privacy" className="hover:text-primary" data-testid="footer-privacy-link">Privacy Policy</Link></li>
                <li><Link to="/refund" className="hover:text-primary" data-testid="footer-refund-link">Refund Policy</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-border/60">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between text-xs text-muted-foreground flex-wrap gap-2">
              <span>© {new Date().getFullYear()} Radiance Hub. All rights reserved.</span>
              <span className="tracking-[0.18em] uppercase">Glow softly. Glow always.</span>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}

